import {
  ArrowRightLeft, Loader2, Search, Plus, X,
} from "lucide-react";
import { Button } from "@/shared/ui/Button";
import { Checkbox } from "@/shared/ui/Checkbox";
import { Input } from "@/shared/ui/Input";
import { formatAmount } from "./utils";
import type { Settlement } from "../../types/accounting.types";

const formatDate = (value: string | null) => {
  if (!value) return "—";
  const d = new Date(value);
  return d.toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric" });
};

type MovementSettlementsTabProps = {
  associatedSettlements: Settlement[]
  isLoadingAssociated: boolean
  searchQuery: string
  onSearchChange: (value: string) => void
  searchResults: Settlement[]
  isSearching: boolean
  associatingId: string | null
  disassociatingId: string | null
  onAssociate: (settlementId: string) => void
  onDisassociate: (settlementId: string) => void
  onOpenSettlement: (settlementId: string) => void
  isMovementLocked: boolean
  // Selection mode
  isSelectionMode?: boolean
  selectedSettlementIds?: Set<string>
  onToggleSettlementSelect?: (id: string) => void
  onOpenMoveDialog?: () => void
  onClearSelection?: () => void
};

const SettlementCard = ({
  settlement,
  onOpen,
  onDisassociate,
  isDisassociating,
  isLocked,
  isSelectionMode = false,
  isSelected = false,
  onToggleSelect,
}: {
  settlement: Settlement
  onOpen: () => void
  onDisassociate?: () => void
  isDisassociating: boolean
  isLocked: boolean
  isSelectionMode?: boolean
  isSelected?: boolean
  onToggleSelect?: () => void
}) => (
  <div
    className={`rounded-md border p-3 cursor-pointer transition-colors ${isSelected ? "border-indigo-400 bg-indigo-100/80 hover:bg-indigo-100" : "border-indigo-200 bg-indigo-50/60 hover:bg-indigo-100/60"}`}
    onClick={onOpen}
  >
    <div className="flex items-start justify-between gap-2">
      {isSelectionMode && !isLocked && (
        <div className="pt-0.5 shrink-0" onClick={(e) => e.stopPropagation()}>
          <Checkbox
            checked={isSelected}
            onCheckedChange={() => onToggleSelect?.()}
            className="size-3.5"
          />
        </div>
      )}
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline gap-2">
          <span className="text-[12px] font-semibold text-indigo-900">
            N°
            {" "}
            {settlement.number || settlement.id}
          </span>
          {settlement.paymentMethod && (
            <span className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium bg-indigo-200/70 text-indigo-900">
              {settlement.paymentMethod}
            </span>
          )}
        </div>
        {(settlement.clientName || settlement.issuer) && (
          <div className="text-[11px] text-indigo-700 mt-0.5">
            {settlement.clientName ?? settlement.issuer}
            {settlement.clientName && settlement.issuer && settlement.clientName !== settlement.issuer && (
              <>
                {" "}
                —
                {settlement.issuer}
              </>
            )}
          </div>
        )}
        <div className="text-[11px] text-indigo-700 mt-0.5">
          {formatDate(settlement.date)}
          {" — "}
          Montant :
          {" "}
          {formatAmount(settlement.amount ?? 0)}
          {" "}
          €
          {settlement.remainingAmount !== null && settlement.remainingAmount !== settlement.amount && (
            <>
              {" "}
              — Solde :
              {formatAmount(settlement.remainingAmount)}
              {" "}
              €
            </>
          )}
        </div>
      </div>
      {!isLocked && onDisassociate && (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-7 w-7 p-0 shrink-0 text-slate-400 hover:text-red-600 hover:bg-red-50"
          disabled={isDisassociating}
          onClick={(e) => {
            e.stopPropagation();
            onDisassociate();
          }}
        >
          {isDisassociating
            ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            )
            : (
              <X className="h-3.5 w-3.5" />
            )}
        </Button>
      )}
    </div>
  </div>
);

const SearchResultCard = ({
  settlement,
  onAssociate,
  isAssociating,
}: {
  settlement: Settlement
  onAssociate: () => void
  isAssociating: boolean
}) => (
  <div className="flex items-center gap-2 rounded-md border border-slate-200 bg-white px-3 py-2">
    <div className="flex-1 min-w-0">
      <div className="flex items-baseline gap-2">
        <span className="text-[12px] font-medium text-slate-800">
          N°
          {" "}
          {settlement.number || settlement.id}
        </span>
        {settlement.paymentMethod && (
          <span className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium bg-slate-100 text-slate-600">
            {settlement.paymentMethod}
          </span>
        )}
      </div>
      <div className="text-[11px] text-slate-500">
        {settlement.clientName && (
          <>
            {settlement.clientName}
            {" "}
            —
            {" "}
          </>
        )}
        {!settlement.clientName && settlement.issuer && (
          <>
            {settlement.issuer}
            {" "}
            —
            {" "}
          </>
        )}
        {settlement.clientName && settlement.issuer && settlement.clientName !== settlement.issuer && (
          <>
            {settlement.issuer}
            {" "}
            —
            {" "}
          </>
        )}
        {formatDate(settlement.date)}
        {" — "}
        {formatAmount(settlement.amount ?? 0)}
        {" "}
        €
      </div>
    </div>
    <Button
      type="button"
      variant="success"
      size="sm"
      className="h-7 px-2.5 text-[11px] shrink-0"
      disabled={isAssociating}
      onClick={onAssociate}
    >
      {isAssociating
        ? (
          <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />
        )
        : (
          <Plus className="h-3.5 w-3.5 mr-1" />
        )}
      Associer
    </Button>
  </div>
);

const MovementSettlementsTab = ({
  associatedSettlements,
  isLoadingAssociated,
  searchQuery,
  onSearchChange,
  searchResults,
  isSearching,
  associatingId,
  disassociatingId,
  onAssociate,
  onDisassociate,
  onOpenSettlement,
  isMovementLocked,
  isSelectionMode = false,
  selectedSettlementIds,
  onToggleSettlementSelect,
  onOpenMoveDialog,
  onClearSelection,
}: MovementSettlementsTabProps) => {
  return (
    <div className="space-y-4">
      {/* Search to associate */}
      {!isMovementLocked && (
        <div className="space-y-2">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              value={searchQuery}
              onChange={(e) => onSearchChange(e.target.value)}
              placeholder="Rechercher un règlement à associer..."
              className="pl-9 h-9 text-[13px]"
            />
            {isSearching && (
              <Loader2 className="absolute right-2.5 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-slate-400" />
            )}
          </div>

          {!isSearching && searchQuery.trim().length >= 1 && searchResults.length === 0 && (
            <div className="text-center py-4 text-[12px] text-slate-500">
              Aucun règlement non associé trouvé.
            </div>
          )}

          {!isSearching && searchResults.map((settlement) => (
            <SearchResultCard
              key={settlement.id}
              settlement={settlement}
              onAssociate={() => onAssociate(settlement.id)}
              isAssociating={associatingId === settlement.id}
            />
          ))}
        </div>
      )}

      {/* Associated settlements */}
      <div className="space-y-2">
        <div className="text-[12px] font-medium text-slate-700">
          Règlements associés
          {!isLoadingAssociated && associatedSettlements.length > 0 && (
            <span className="ml-1 text-slate-400">
              (
              {associatedSettlements.length}
              )
            </span>
          )}
        </div>

        {isLoadingAssociated && (
          <div className="flex items-center justify-center py-6">
            <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
          </div>
        )}

        {!isLoadingAssociated && associatedSettlements.length === 0 && (
          <div className="text-center py-4 text-[12px] text-slate-400">
            Aucun règlement associé à ce mouvement.
          </div>
        )}

        {!isLoadingAssociated && associatedSettlements.map((settlement) => (
          <SettlementCard
            key={settlement.id}
            settlement={settlement}
            onOpen={() => onOpenSettlement(settlement.id)}
            onDisassociate={() => onDisassociate(settlement.id)}
            isDisassociating={disassociatingId === settlement.id}
            isLocked={isMovementLocked}
            isSelectionMode={isSelectionMode}
            isSelected={selectedSettlementIds?.has(settlement.id) ?? false}
            onToggleSelect={() => onToggleSettlementSelect?.(settlement.id)}
          />
        ))}
      </div>

      {isSelectionMode && selectedSettlementIds && selectedSettlementIds.size > 0 && (
        <div className="mt-3 flex items-center gap-3 rounded-md border border-indigo-200 bg-indigo-50 px-3 py-2">
          <span className="text-[11px] font-medium text-indigo-800">
            {selectedSettlementIds.size}
            {" "}
            règlement(s) sélectionné(s)
          </span>
          <div className="flex-1" />
          <button
            type="button"
            className="text-[11px] text-indigo-600 hover:text-indigo-800 underline"
            onClick={onClearSelection}
          >
            Désélectionner
          </button>
          <Button
            type="button"
            size="sm"
            className="h-7 px-2.5 text-[11px] gap-1.5"
            onClick={onOpenMoveDialog}
          >
            <ArrowRightLeft className="h-3 w-3" />
            Déplacer vers...
          </Button>
        </div>
      )}
    </div>
  );
};

export default MovementSettlementsTab;
